/* eslint-disable sonarjs/no-duplicate-string */
import {
    AVAILABILITY_ZONE_NAMES,
    AvailabilityZonesUtils,
    DISABLED_AVAILABILITY_ZONE_NAMES,
} from 'infra/helpers/availabilityZonesUtils';
import * as R from 'ramda';
import { formatNum } from 'system/libs/stringHelper';

import { Environment } from 'Environment';

// #region Types
type PhysicalDisk = 'hdd' | 'ssd' | 'highIops';

type Volumes =
    | 'ko1-high-iops'
    | 'dp1-high-iops'
    | 'ko1-ssd'
    | 'dp1-ssd'
    | 'ssd'
    | 'ms1'
    | 'dp1'
    | 'ceph'
    // omega:
    | 'MoscowEast-hdd'
    | 'MoscowEast-ssd'
    | 'local-ssd'
    | 'Standard-hdd';

type VolumeData = {
    name: string;
    shortname: string;
    region?: string;
    geo?: boolean;
};
// #endregion

const { MS1, DP1 } = AVAILABILITY_ZONE_NAMES; // MS1 - Москва-Север, DP1 - Москва-Восток

const VOLUMES: Record<Volumes, VolumeData> = {
    'ko1-high-iops': {
        name: 'High-IOPS SSD',
        shortname: 'High-IOPS SSD',
        region: MS1,
    },
    'dp1-high-iops': {
        name: 'High-IOPS SSD',
        shortname: 'High-IOPS SSD',
        region: DP1,
    },
    'ko1-ssd': {
        name: 'Сетевой SSD-диск',
        shortname: 'Сетевой SSD',
        region: MS1,
    },
    'dp1-ssd': {
        name: 'Сетевой SSD-диск',
        shortname: 'Сетевой SSD',
        region: DP1,
    },
    ssd: {
        name: 'Сетевой SSD-диск c гео-репликацией',
        shortname: 'SSD c гео-репликацией',
        geo: true,
    },
    ms1: {
        name: 'Сетевой HDD-диск',
        shortname: 'Сетевой HDD',
        region: MS1,
    },
    dp1: {
        name: 'Сетевой HDD-диск',
        shortname: 'Сетевой HDD',
        region: DP1,
    },
    ceph: {
        name: 'Сетевой HDD-диск c гео-репликацией',
        shortname: 'HDD c гео-репликацией',
        geo: true,
    },
    'MoscowEast-hdd': {
        name: 'Сетевой HDD-диск',
        shortname: 'Сетевой HDD',
        region: MS1,
    },
    'MoscowEast-ssd': {
        name: 'Сетевой SSD-диск',
        shortname: 'Сетевой SSD',
        region: DP1,
    },
    'local-ssd': {
        name: 'Локальный SSD-диск',
        shortname: 'Локальный SSD',
        region: DP1,
    },
    'Standard-hdd': {
        name: 'Сетевой HDD-диск c гео-репликацией',
        shortname: 'HDD c гео-репликацией',
        geo: true,
    },
};

const DISK_REPLICATION_TYPES_BY_PHYSICAL_DISK_TYPES: Record<
    PhysicalDisk,
    Volumes[]
> = {
    hdd: ['ms1', 'dp1', 'ceph', 'MoscowEast-hdd', 'Standard-hdd'],
    highIops: ['ko1-high-iops', 'dp1-high-iops'],
    ssd: ['ko1-ssd', 'dp1-ssd', 'ssd', 'local-ssd', 'MoscowEast-ssd'],
};

// TODO: смержить с VOLUMES
const VOLUME_TYPES_BY_ZONE: Record<'DP1' | 'MS1', Volumes[]> = {
    DP1: [
        'dp1',
        'dp1-ssd',
        'local-ssd',
        'dp1-high-iops',
        'ceph',
        'ssd',
        'MoscowEast-hdd',
        'MoscowEast-ssd',
        'Standard-hdd',
    ],
    MS1: ['ms1', 'ko1-ssd', 'ko1-high-iops', 'ceph', 'ssd', 'Standard-hdd'],
};

const PROD_DISK_TYPES: Volumes[] = [
    'ko1-high-iops',
    'dp1-high-iops',
    'ko1-ssd',
    'dp1-ssd',
    'ssd',
    'ms1',
    'dp1',
    'ceph',
];
const STAGE_DISK_TYPES: Volumes[] = [
    'ko1-high-iops',
    'dp1-high-iops',
    'MoscowEast-hdd',
    'MoscowEast-ssd',
    'local-ssd',
    'Standard-hdd',
    'ssd',
];

const KNOWN_DISK_TYPES = Object.keys(VOLUMES) as Volumes[];

export const GEO_DISTRIBUTION_DISK_TYPES = Object.entries(VOLUMES)
    .filter(([, item]) => item.geo)
    .map(([name]) => name);

export const LOCAL_DISK_TYPES = Object.entries(VOLUMES)
    .filter(([, item]) => !item.geo)
    .map(([name]) => name);

// TODO: смержить с VOLUMES
const DEFAULT_DISK_TYPES_BY_ENVIRONMENT = {
    productionHost: {
        ssd: {
            MS1: 'ko1-ssd',
            DP1: 'dp1-ssd',
        },
        hdd: {
            MS1: 'ms1',
            DP1: 'dp1',
        },
    },
    stageHost: {
        ssd: {
            MoscowEast: 'MoscowEast-ssd',
        },
        hdd: {
            MoscowEast: 'Standard-hdd',
        },
    },
    private: { internal: 'ibm-svc' }, // can be ceph?
};

const checkNameContainsSsd = (name: string) => /ssd/i.test(name);

export const VolumeTypeUtils = {
    checkNameContainsSsd,

    getFirstDiskReplicationType(type: PhysicalDisk): Volumes {
        return DISK_REPLICATION_TYPES_BY_PHYSICAL_DISK_TYPES[type][0];
    },

    convertToPhysicalDiskType(rawType: Volumes): PhysicalDisk {
        const volumesWithIops: Volumes[] = ['ko1-high-iops', 'dp1-high-iops'];
        if (volumesWithIops.includes(rawType)) {
            return 'highIops';
        }

        return checkNameContainsSsd(rawType) ? 'ssd' : 'hdd';
    },

    getVolumeTypeRegionName(volumeType: Volumes) {
        const region = VOLUMES[volumeType]?.region;

        if (!region && Environment.isPrivateBuild) {
            return '—';
        }

        return region
            ? AvailabilityZonesUtils.getHumanReadableName(region)
            : 'Москва';
    },

    getVolumeTypeRegion(volumeType: Volumes) {
        return VOLUMES[volumeType]?.region;
    },

    getVolumeTypeName(volumeType: Volumes) {
        return VOLUMES[volumeType]?.name || volumeType;
    },

    getVolumeTypeShortName(volumeType: Volumes) {
        return VOLUMES[volumeType]?.shortname || volumeType;
    },

    matchVolumeTypeAndZone(volumeType: Volumes, availabilityZone) {
        return VOLUMES[volumeType]?.region === availabilityZone;
    },

    getDiskTypeOptions(excludedDiskTypes: PhysicalDisk[]) {
        const excluded = excludedDiskTypes || [];
        const defaultTypes = [
            {
                label: 'HDD',
                value: 'hdd' as PhysicalDisk,
                dataTest: 'radio-disk-type-hdd',
            },
            {
                label: 'SSD',
                value: 'ssd' as PhysicalDisk,
                dataTest: 'radio-disk-type-ssd',
            },
        ];

        const highIopsType = {
            label: 'High-IOPS SSD',
            value: 'highIops' as PhysicalDisk,
            dataTest: 'radio-disk-type-high-ssd',
        };

        return [...defaultTypes, highIopsType].filter(
            (type) => !excluded.includes(type.value),
        );
    },

    getDiskDescription(prices, excludedDiskTypes) {
        const excluded = excludedDiskTypes || [];
        const getPriceInfo = (diskType) => {
            if (prices === null) {
                return '';
            }

            const { [diskType]: diskTypePrice } = prices;

            const formattedPrice = formatNum(diskTypePrice, {
                zeroToFree: false,
                addRoubleSign: true,
                onlyDecimalToFixed: 2,
            });

            return `Стоимость - ${formattedPrice} за ГБ в мес.`;
        };

        const descriptionsByDiskType = {
            hdd: `<b>HDD</b> – обычный магнитный жесткий диск. Подойдет для хранения больших объемов информации. ${getPriceInfo(
                'hdd',
            )}`,
            ssh: `<b>SSD</b> – твердотельный накопитель. Высокая скорость считывания и записи информации. Производительность выше, чем у HDD. ${getPriceInfo(
                'ssd',
            )}`,
            high_iops_disk: `Для приложений с повышенными требованиями к <b>High-IOPS SSD</b>. Диск не поддерживает георепликацию. ${getPriceInfo(
                'high_iops_disk',
            )}`,
        };

        return Object.keys(descriptionsByDiskType)
            .filter((diskType) => !excluded.includes(diskType))
            .map((key) => descriptionsByDiskType[key]);
    },

    getDiskRepicationTypesByZoneAndPhysicalType({
        availabilityZoneName,
        type,
        volumeTypes,
    }) {
        const volumeTypesOfZone =
            VOLUME_TYPES_BY_ZONE[availabilityZoneName] ||
            VOLUME_TYPES_BY_ZONE.MS1;
        const volumeTypesOfPhysicalDiskType =
            DISK_REPLICATION_TYPES_BY_PHYSICAL_DISK_TYPES[type] ||
            DISK_REPLICATION_TYPES_BY_PHYSICAL_DISK_TYPES.ssd;

        return volumeTypes.filter((volumeType) => {
            let inAvailabilityZone = true;
            // костыль для омеги, там сейчас все равно только одна зона
            if (Environment.isProductionHost) {
                inAvailabilityZone = volumeTypesOfZone.find(
                    (disk) => disk === volumeType.name,
                );
            }
            const inPhysicalDiskType = volumeTypesOfPhysicalDiskType.find(
                (disk) => disk === volumeType.name,
            );

            return inAvailabilityZone && inPhysicalDiskType;
        });
    },

    getPhysicalDiskType(type: Volumes): PhysicalDisk {
        if (
            DISK_REPLICATION_TYPES_BY_PHYSICAL_DISK_TYPES.hdd.find(
                (diskType) => diskType === type,
            )
        ) {
            return 'hdd';
        }
        if (
            DISK_REPLICATION_TYPES_BY_PHYSICAL_DISK_TYPES.highIops.find(
                (diskType) => diskType === type,
            )
        ) {
            return 'highIops';
        }
        return 'ssd';
    },

    getReplicationDiskType({
        type,
        availabilityZoneName,
        isGeoReplication,
        volumeTypes,
    }) {
        const diskRepicationTypesByZoneAndPhysicalType = this.getDiskRepicationTypesByZoneAndPhysicalType(
            {
                availabilityZoneName,
                type,
                volumeTypes,
            },
        ).filter((disk) => KNOWN_DISK_TYPES.includes(disk.name));

        if (type === 'highIops') {
            return diskRepicationTypesByZoneAndPhysicalType[0]
                ? diskRepicationTypesByZoneAndPhysicalType[0].name
                : null;
        }
        const allowedDiskTypes = isGeoReplication
            ? GEO_DISTRIBUTION_DISK_TYPES
            : LOCAL_DISK_TYPES;

        const diskRepicationType = diskRepicationTypesByZoneAndPhysicalType.find(
            (disk) => allowedDiskTypes.includes(disk.name),
        );

        return diskRepicationType ? diskRepicationType.name : null;
    },

    filterDiskTypesByAvailableZones(types) {
        if (Environment.isPrivateBuild) {
            return types;
        }

        return types.filter(({ name }) =>
            Object.keys(VOLUME_TYPES_BY_ZONE).some(
                (zoneName) =>
                    !DISABLED_AVAILABILITY_ZONE_NAMES.includes(
                        zoneName.toLowerCase(),
                    ) && VOLUME_TYPES_BY_ZONE[zoneName].includes(name),
            ),
        );
    },

    getIsControlTypeDropdown(volumeTypes) {
        if (Environment.isPrivateBuild) {
            return true;
        }

        const DISK_TYPES = Environment.isProductionHost
            ? PROD_DISK_TYPES
            : STAGE_DISK_TYPES;

        return !DISK_TYPES.every((type) => volumeTypes.includes(type));
    },

    getOptionsForSelect({ volumeTypes, availabilityZoneName }) {
        if (Environment.isProductionHost) {
            return volumeTypes.filter((item) =>
                VOLUME_TYPES_BY_ZONE[availabilityZoneName].includes(item.name),
            );
        }
        return volumeTypes;
    },

    getDiskTypeForSelect({ availabilityZoneName, physicalDisk }) {
        if (Environment.isPrivateBuild) {
            return DEFAULT_DISK_TYPES_BY_ENVIRONMENT.private[
                availabilityZoneName
            ];
        }
        const hostType = Environment.isProductionHost
            ? 'productionHost'
            : 'stageHost';

        return R.path(
            [hostType, physicalDisk, availabilityZoneName],
            DEFAULT_DISK_TYPES_BY_ENVIRONMENT,
        );
    },
};
